import React, { useState, useEffect } from 'react';
import { Grid, Container } from '@material-ui/core';
import {
  BoxV2 as Box,
  Flex,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
} from 'portal-commons';

import BrandListingRow from '../../components/BrandListingRow';
import { Loader, FilterToggleButton } from '../../../../../shared_elements';
import { findAllIdentityStatuses } from '../../apiServices';
import '../../assets/brands-listing-module.scss';
import { ENTITY_TYPES } from '../../../../../constants';
import { fieldValidation } from '../../../../../utils/validator';
import {
  getCspNameAutocompleteOptions,
  getBrandNameAutocompleteOptions,
} from '@/apis/services';
import { cspController, brandController } from '@/apis/core';

const FILTER_CONFIG = {
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  companyName: {
    type: FilterType.Text,
    label: 'Brand Legal Name',
    placeholder: 'Enter Brand Legal Name',
    suggestions: [],
  },
  displayName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA name',
    suggestions: [],
  },
  entityType: {
    type: FilterType.Dropdown,
    options: ENTITY_TYPES.map((entity) => ({
      label: entity.value,
      value: entity.value,
    })),
    label: 'Entity Type',
    width: 200,
  },
  ein: {
    type: FilterType.Text,
    label: 'EIN/Tax number',
    placeholder: 'Enter EIN/Tax number',
  },
  universalEin: {
    type: FilterType.Text,
    label: 'Universal EIN',
    placeholder: 'Enter Universal EIN',
    error: false,
    helperText: '',
  },
  identityStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Identity Status',
    width: 200,
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deleted', value: 'DELETED' },
    ],
    label: 'TCR Status',
    width: 200,
  },
};

const FilterErrorCode = {
  universalEin: {
    0: '',
    5: 'Invalid input',
  },
  universalEinObj: {
    xssProtection: true,
  },
};

const headRows = [
  { id: 'uid', label: 'BRAND ID', sortable: true },
  { id: 'companyName', label: 'BRAND LEGAL NAME', sortable: true },
  { id: 'displayName', label: 'BRAND DBA NAME', sortable: true },
  { id: 'cspName', label: 'CSP DBA NAME', sortable: true },
  { id: 'entityType', label: 'ENTITY TYPE', sortable: true },
  { id: 'universalEin', label: 'UNIVERSAL EIN', sortable: false },
  { id: 'status', label: 'TCR Status', sortable: false },
  { id: 'activeCampaignCount', label: 'N. OF CAMPAIGNS', sortable: false },
];

const FILTER_LOADERS = {
  suggestion: {
    displayName: {
      minLength: 2,
      load: (value) => getBrandNameAutocompleteOptions('displayName', value),
    },
    cspName: {
      minLength: 2,
      load: (value) => getCspNameAutocompleteOptions('displayName', value),
    },
    companyName: {
      minLength: 2,
      load: (value) => getBrandNameAutocompleteOptions('companyName', value),
    },
  },
};

const MAX_DISPLAY_RECORDS = 10000;

const BrandsListing = () => {
  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    setOptions,
    handleEdit,
    handleApply,
    setConfig,
  } = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const table = useTable(
    async () => {
      const query = getQuery();
      return fetchBrandsList(query);
    },
    {
      cappingTotal: MAX_DISPLAY_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const getQuery = () => {
    return {
      ...table.filter,
      ...appliedFilters,
      ...additionalFilters,
    };
  };

  const [isFilterVisible, setIsFilterVisible] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    const identityStatuses = await findAllIdentityStatuses();
    if (identityStatuses?.length > 0) {
      setOptions(
        'identityStatus',
        identityStatuses.map((status) => ({ label: status, value: status }))
      );
    }
  };

  const fetchBrandsList = async (queries = {}) => {
    const additionalQueries = { ...additionalFilters };
    if (queries.cspName) {
      const suggestions = await cspController.getCspSuggestions({
        prefix: queries.cspName,
        limit: 20,
      });
      if (suggestions.length > 0) {
        additionalQueries.cspUid = suggestions[0].cspUid;
      }
    }
    const response = await brandController.getBrands({
      ...queries,
      ...additionalQueries,
    });
    return {
      data: response?.records || [],
      total: response?.totalRecords || 0,
      rowsPerPage: response?.recordsPerPage || 10,
      page: response?.page || 1,
    };
  };

  const resetFilterConfigsError = (newValues) => {
    Object.keys(newValues).forEach((key) => {
      if (
        filterConfigs[key]?.error &&
        newValues[key] !== candidateFilters[key]
      ) {
        setConfig(key, { ...filterConfigs[key], error: false, helperText: '' });
      }
    });
  };

  const validateFilters = (values) => {
    let isValid = Object.keys(values).every((key) => !filterConfigs[key].error);
    Object.keys(values).forEach((key) => {
      if (FilterErrorCode[key]) {
        const code = fieldValidation({
          ...FilterErrorCode[`${key}Obj`],
          fieldval: values[key],
        });
        if (code !== 0) {
          isValid = false;
          setConfig(key, {
            ...filterConfigs[key],
            error: true,
            helperText: FilterErrorCode[key][code],
          });
        }
      }
    });
    return isValid;
  };

  const handleCandidateFiltersChange = (values = {}) => {
    resetFilterConfigsError(values);
    handleEdit(values);
  };

  const handleAppliedFiltersChange = (values = {}) => {
    if (!validateFilters(values)) {
      return;
    }

    if (Object.keys(values).length === 0) {
      resetFilterConfigsError(filterConfigs);
    }
    handleApply(values);
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <section className="brands-listing-section page-content">
        {table.pageLoading ? (
          <Loader />
        ) : (
          <Container
            maxWidth={false}
            className="brands-listing-container"
            data-testid="brandsListing"
          >
            <Box>
              <div className="title-block">
                <h2>
                  {table.total === 0 ? 'Brands' : `${table.total} Brands`}
                </h2>
                <span>
                  Results capped at first{' '}
                  {MAX_DISPLAY_RECORDS.toLocaleString('en-US')} records
                </span>
              </div>
            </Box>
            <Flex
              sx={{
                flexDirection: 'column',
                marginTop: 's',
                marginBottom: 's',
              }}
            >
              <Box>
                <FilterToggleButton
                  visible={isFilterVisible}
                  onClick={toggleFilterVisibility}
                />
              </Box>
              {isFilterVisible && (
                <TableFilter
                  portal="mno"
                  configs={filterConfigs}
                  candidateValues={candidateFilters}
                  appliedValues={appliedFilters}
                  onCandidateValuesChange={handleCandidateFiltersChange}
                  onAppliedValuesChange={handleAppliedFiltersChange}
                  className="filters"
                  data-testid="brands"
                />
              )}
            </Flex>
            <Grid container justifyContent="center" spacing={0}>
              <Table
                testId="brandsTable"
                className="brands-table"
                loading={table.loading}
                headRows={headRows}
                emptyState="no brands to view"
                data={table.tableData}
                rowKey="uid"
                renderRow={(data) => (
                  <BrandListingRow key={data.uid} brand={data} />
                )}
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                filter={table.filter}
                pagination={table.pagination}
              />
            </Grid>
          </Container>
        )}
      </section>
    </Box>
  );
};

export default BrandsListing;
